import React, { useState, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { useStore } from '../../context/StoreContext';
import { downloadPdf } from '../../utils/downloadPdf';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Badge } from '../../components/ui/badge';
import { Label } from '../../components/ui/label';
import { Switch } from '../../components/ui/switch';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle
} from '../../components/ui/dialog';
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow
} from '../../components/ui/table';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue
} from '../../components/ui/select';
import {
  ShoppingCart, Eye, Package, Truck, CheckCircle, XCircle, Clock,
  ArrowLeft, PaperPlaneTilt, CircleNotch, Plus, ChatCircleDots,
  MapPin, CurrencyEur, Timer, FilePdf, Download, MagnifyingGlass, CaretRight, Star
} from '@phosphor-icons/react';
import { PaymentMethodBadge } from '../../components/PaymentMethodBadge';
import axios from 'axios';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const statusConfig = {
  pending: { label: 'Commande créée', color: 'bg-slate-100 text-slate-700', icon: Clock },
  'on-hold': { label: 'Commande créée', color: 'bg-slate-100 text-slate-700', icon: Clock },
  paid: { label: 'Paiement vérifié', color: 'bg-emerald-100 text-emerald-800', icon: CheckCircle },
  confirmed: { label: 'Paiement vérifié', color: 'bg-emerald-100 text-emerald-800', icon: CheckCircle },
  processing: { label: 'En préparation', color: 'bg-blue-100 text-blue-800', icon: Package },
  preparing: { label: 'En préparation', color: 'bg-blue-100 text-blue-800', icon: Package },
  shipping: { label: 'Expédiée', color: 'bg-purple-100 text-purple-800', icon: Truck },
  delivered: { label: 'Livrée', color: 'bg-emerald-100 text-emerald-800', icon: CheckCircle },
  completed: { label: 'Livrée', color: 'bg-emerald-100 text-emerald-800', icon: CheckCircle },
  cancelled: { label: 'Annulée', color: 'bg-red-100 text-red-800', icon: XCircle },
  refunded: { label: 'Remboursée', color: 'bg-orange-100 text-orange-800', icon: XCircle },
  failed: { label: 'Échouée', color: 'bg-red-100 text-red-800', icon: XCircle },
};

// ======== ORDER DETAIL WITH TIMELINE ========
const OrderDetail = ({ order, storeId, onClose, onUpdate }) => {
  const [historyNote, setHistoryNote] = useState('');
  const [adding, setAdding] = useState(false);
  const [updating, setUpdating] = useState(false);
  const [newStatus, setNewStatus] = useState(order.status);
  const [trackingNumber, setTrackingNumber] = useState(order.tracking_number || '');

  const addHistory = async () => {
    if (!historyNote.trim()) return;
    setAdding(true);
    try {
      await axios.post(`${API_URL}/api/stores/${storeId}/orders/${order.id}/history`,
        { status: order.status, notes: historyNote },
        { withCredentials: true }
      );
      toast.success('Note ajoutée');
      setHistoryNote('');
      onUpdate();
    } catch { toast.error('Erreur'); }
    finally { setAdding(false); }
  };

  const updateStatus = async (targetStatus, note) => {
    setUpdating(true);
    try {
      await axios.put(`${API_URL}/api/stores/${storeId}/orders/${order.id}/status`,
        { status: targetStatus, tracking_number: trackingNumber, notes: note || `Statut changé vers ${statusConfig[targetStatus]?.label || targetStatus}` },
        { withCredentials: true }
      );
      toast.success('Statut mis à jour — client notifié');
      setNewStatus(targetStatus);
      onUpdate();
    } catch { toast.error('Erreur'); }
    finally { setUpdating(false); }
  };

  const toggleTest = async () => {
    setUpdating(true);
    try {
      const { data } = await axios.post(
        `${API_URL}/api/stores/${storeId}/orders/${order.id}/mark-test`,
        { is_test: !order.is_test }, { withCredentials: true }
      );
      toast.success(data.is_test ? 'Commande marquée comme TEST (exclue des stats)' : 'Commande retirée des tests');
      onUpdate();
    } catch { toast.error('Erreur'); }
    finally { setUpdating(false); }
  };

  const verifyStripe = async () => {
    setUpdating(true);
    try {
      const { data } = await axios.post(
        `${API_URL}/api/stores/${storeId}/orders/${order.id}/verify-stripe`,
        {}, { withCredentials: true, timeout: 15000 }
      );
      if (data.verified) {
        toast.success(`Paiement vérifié auprès de Stripe (${data.amount.toFixed(2)} €). Client notifié.`);
      } else {
        toast.error(`Stripe indique: ${data.stripe_payment_status}. Aucune confirmation possible.`);
      }
      onUpdate();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Erreur lors de la vérification Stripe');
    } finally { setUpdating(false); }
  };

  const verifyPayPal = async () => {
    setUpdating(true);
    try {
      const { data } = await axios.post(
        `${API_URL}/api/stores/${storeId}/orders/${order.id}/verify-paypal`,
        {}, { withCredentials: true, timeout: 20000 }
      );
      if (data.verified) {
        toast.success(`Paiement vérifié auprès de PayPal (${data.amount.toFixed(2)} €). Client notifié.`);
      } else {
        toast.error(`PayPal indique: ${data.paypal_payment_status}. Aucune confirmation possible.`);
      }
      onUpdate();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Erreur lors de la vérification PayPal');
    } finally { setUpdating(false); }
  };

  const requestReview = async () => {
    setUpdating(true);
    try {
      const { data } = await axios.post(
        `${API_URL}/api/stores/${storeId}/orders/${order.id}/request-review`,
        {}, { withCredentials: true, timeout: 20000 }
      );
      toast.success(`Demande d'avis envoyée à ${data.to}`);
      onUpdate();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Erreur envoi demande d\'avis');
    } finally { setUpdating(false); }
  };

  const status = statusConfig[order.status] || statusConfig.pending;
  const history = order.history || [];

  return (
    <div className="space-y-6" data-testid="order-detail">
      {/* Header */}
      {/* Header row: title left, dual status pills right */}
      <div className="flex flex-col md:flex-row md:items-start md:justify-between gap-4">
        <div className="flex items-center gap-3">
          <Button variant="ghost" size="icon" onClick={onClose} className="rounded-full"><ArrowLeft size={20} /></Button>
          <div>
            <p className="text-xs text-slate-400 mb-0.5"><button onClick={onClose} className="hover:underline">← Commandes</button></p>
            <h2 className="text-3xl font-black tracking-tight">Commande {order.order_number}</h2>
            <p className="text-sm text-slate-500 mt-0.5">{new Date(order.created_at).toLocaleString('fr-FR')}</p>
          </div>
        </div>
        {(() => {
          const paid = order.payment_status === 'paid' || ['paid', 'completed'].includes(order.status);
          const failed = order.payment_status === 'failed' || order.status === 'cancelled' || order.status === 'failed';
          const stripeVerified = !!order.stripe_verified_at;
          const bucket = ['delivered', 'completed'].includes(order.status) ? 'Livrée'
            : ['shipping', 'shipped'].includes(order.status) ? 'Expédiée'
            : ['preparing', 'processing'].includes(order.status) ? 'En préparation'
            : order.status === 'cancelled' ? 'Annulée'
            : order.status === 'refunded' ? 'Remboursée'
            : (paid ? 'Prête à préparer' : 'En attente paiement');
          // Shipping method badge
          const sm = (order.shipping_method || '').toLowerCase();
          const shipMap = {
            pickup: { label: 'Retrait boutique', icon: '🏪', cls: 'bg-emerald-50 text-emerald-800 border-emerald-200' },
            retrait: { label: 'Retrait boutique', icon: '🏪', cls: 'bg-emerald-50 text-emerald-800 border-emerald-200' },
            mondial_relay: { label: 'Point Relais', icon: '📦', cls: 'bg-sky-50 text-sky-800 border-sky-200' },
            point_relais: { label: 'Point Relais', icon: '📦', cls: 'bg-sky-50 text-sky-800 border-sky-200' },
            local_delivery: { label: 'Livraison locale', icon: '🚚', cls: 'bg-indigo-50 text-indigo-800 border-indigo-200' },
            standard: { label: 'Livraison standard', icon: '🚚', cls: 'bg-indigo-50 text-indigo-800 border-indigo-200' },
            livraison: { label: 'Livraison', icon: '🚚', cls: 'bg-indigo-50 text-indigo-800 border-indigo-200' },
            delivery: { label: 'Livraison', icon: '🚚', cls: 'bg-indigo-50 text-indigo-800 border-indigo-200' },
            express: { label: 'Livraison express', icon: '⚡', cls: 'bg-rose-50 text-rose-800 border-rose-200' },
          };
          const capitalize = (s) => s ? s.charAt(0).toUpperCase() + s.slice(1) : '';
          const ship = shipMap[sm] || { label: capitalize(order.shipping_method) || 'Livraison', icon: '🚚', cls: 'bg-slate-100 text-slate-700 border-slate-200' };
          return (
            <div className="flex gap-2 flex-wrap items-center">
              <span className={`px-3 py-1.5 rounded-full text-sm font-semibold border ${ship.cls}`} data-testid="order-shipping-badge">
                <span className="mr-1">{ship.icon}</span>{ship.label}
              </span>
              <PaymentMethodBadge method={order.payment_method} size="md" />
              <span className={`px-3 py-1.5 rounded-full text-sm font-semibold border flex items-center gap-1.5 ${failed ? 'bg-red-50 text-red-700 border-red-200' : paid ? 'bg-emerald-50 text-emerald-800 border-emerald-200' : 'bg-amber-50 text-amber-800 border-amber-200'}`}>
                <span className={`w-2 h-2 rounded-full ${failed ? 'bg-red-500' : paid ? 'bg-emerald-500' : 'bg-amber-500'}`} />
                {failed ? 'Échec paiement' : stripeVerified ? '✓ Vérifié Stripe' : (order.paypal_verified_at ? '✓ Vérifié PayPal' : (paid ? 'Payé' : 'En attente'))}
              </span>
              <span className={`px-3 py-1.5 rounded-full text-sm font-semibold border ${['Annulée','Remboursée'].includes(bucket) ? 'bg-red-50 text-red-700 border-red-200' : bucket === 'Livrée' ? 'bg-emerald-50 text-emerald-800 border-emerald-200' : 'bg-slate-100 text-slate-700 border-slate-200'}`}>
                {bucket}
              </span>
            </div>
          );
        })()}
      </div>

      {/* ═══ Big Payment Status Banner ═══ Impossible to miss before releasing merchandise */}
      {(() => {
        const paid = order.payment_status === 'paid' || ['paid', 'completed'].includes(order.status);
        const failed = order.payment_status === 'failed' || order.status === 'cancelled' || order.status === 'failed';
        const stripeVerified = !!order.stripe_verified_at;
        const paypalVerified = !!order.paypal_verified_at;
        const isStripeOrder = !!order.stripe_session_id;
        const isPaypalOrder = !!order.paypal_order_id || /paypal/i.test(order.payment_method || '');
        const isCod = /cod|contre.remboursement|espèces/i.test(order.payment_method || '');

        // State machine
        let state = 'unpaid';           // never received payment confirmation
        let stateLabel = 'NON PAYÉ';
        let stateSub = 'Aucune confirmation de paiement reçue';
        let cls = 'from-red-500 to-red-600 text-white ring-red-500/30';
        let icon = '✗';
        if (failed) {
          state = 'failed';
          stateLabel = 'PAIEMENT ÉCHOUÉ';
          stateSub = 'Ne pas expédier la marchandise';
          cls = 'from-red-500 to-red-600 text-white ring-red-500/30';
          icon = '✗';
        } else if (paid && (stripeVerified || paypalVerified)) {
          state = 'verified';
          stateLabel = 'PAYÉ & VÉRIFIÉ';
          stateSub = stripeVerified ? `Confirmé auprès de Stripe le ${new Date(order.stripe_verified_at).toLocaleString('fr-FR')}` : `Confirmé auprès de PayPal le ${new Date(order.paypal_verified_at).toLocaleString('fr-FR')}`;
          cls = 'from-emerald-500 to-emerald-600 text-white ring-emerald-500/30';
          icon = '✓';
        } else if (paid) {
          state = 'paid';
          stateLabel = 'PAYÉ';
          stateSub = isPaypalOrder && !paypalVerified
            ? 'Cliquez sur "Vérifier PayPal" pour confirmer le paiement avant d\'expédier'
            : isStripeOrder && !stripeVerified
              ? 'Cliquez sur "Vérifier Stripe" pour confirmer le paiement avant d\'expédier'
              : 'Paiement reçu';
          cls = 'from-emerald-400 to-emerald-500 text-white ring-emerald-400/30';
          icon = '✓';
        } else if (isCod) {
          state = 'cod';
          stateLabel = 'PAIEMENT À LA LIVRAISON';
          stateSub = 'Encaisser à la remise du colis';
          cls = 'from-amber-500 to-amber-600 text-white ring-amber-500/30';
          icon = '💶';
        }

        return (
          <div
            className={`relative rounded-2xl overflow-hidden bg-gradient-to-br ${cls} shadow-lg ring-4 p-5 md:p-6`}
            data-testid="payment-status-banner"
            data-payment-state={state}
          >
            <div className="flex items-center justify-between gap-4 flex-wrap">
              <div className="flex items-center gap-4 min-w-0">
                <div className="w-14 h-14 md:w-16 md:h-16 rounded-2xl bg-white/25 backdrop-blur-sm flex items-center justify-center text-3xl md:text-4xl font-black">
                  {icon}
                </div>
                <div className="min-w-0">
                  <p className="text-xs md:text-sm uppercase tracking-widest opacity-80 font-semibold">Statut paiement</p>
                  <p className="text-2xl md:text-4xl font-black tracking-tight leading-tight">{stateLabel}</p>
                  <p className="text-xs md:text-sm mt-1 opacity-90">{stateSub}</p>
                </div>
              </div>
              <div className="flex flex-col gap-2 items-end">
                <div className="text-right">
                  <p className="text-xs uppercase tracking-widest opacity-80 font-semibold">Montant</p>
                  <p className="text-2xl md:text-3xl font-black">{(order.total || 0).toFixed(2)} €</p>
                </div>
                <div className="flex gap-2">
                  {isStripeOrder && !stripeVerified && (
                    <Button
                      onClick={verifyStripe}
                      disabled={updating}
                      size="sm"
                      className="bg-white text-slate-900 hover:bg-slate-100 font-bold shadow-md"
                      data-testid="verify-stripe-inline-btn"
                    >
                      {updating ? <CircleNotch size={14} className="animate-spin mr-1.5" /> : <CheckCircle size={14} className="mr-1.5" weight="fill" />}
                      Vérifier Stripe
                    </Button>
                  )}
                  {isPaypalOrder && !paypalVerified && (
                    <Button
                      onClick={verifyPayPal}
                      disabled={updating}
                      size="sm"
                      className="bg-white text-slate-900 hover:bg-slate-100 font-bold shadow-md"
                      data-testid="verify-paypal-inline-btn"
                    >
                      {updating ? <CircleNotch size={14} className="animate-spin mr-1.5" /> : <CheckCircle size={14} className="mr-1.5" weight="fill" />}
                      Vérifier PayPal
                    </Button>
                  )}
                  <Button
                    onClick={toggleTest}
                    disabled={updating}
                    size="sm"
                    variant="outline"
                    className="bg-white/20 border-white/50 text-white hover:bg-white/30 font-semibold"
                    data-testid="toggle-test-order-btn"
                  >
                    {order.is_test ? 'Retirer du TEST' : 'Marquer TEST'}
                  </Button>
                </div>
              </div>
            </div>
          </div>
        );
      })()}

      {/* Vertical Timeline (clickable stages) — condensed & horizontal */}
      <Card className="rounded-2xl">
        <CardHeader className="pb-2 flex flex-row items-center justify-between">
          <CardTitle className="text-base">Suivi de la commande</CardTitle>
          <span className="text-xs text-slate-400">Cliquez sur une étape pour la marquer comme atteinte</span>
        </CardHeader>
        <CardContent className="pt-3">
          {(() => {
            const STAGES = [
              { key: 'pending',    label: 'Commande créée',   note: 'Client a passé la commande' },
              { key: 'confirmed',  label: 'Paiement vérifié', note: 'Confirmé auprès de Stripe' },
              { key: 'preparing',  label: 'En préparation',   note: 'Commande en cours' },
              { key: 'shipping',   label: 'Expédiée',         note: 'En route vers le client' },
              { key: 'delivered',  label: 'Livrée',           note: 'Livraison terminée' },
              { key: 'invoiced',   label: 'Facturée',         note: 'Facture émise' },
            ];
            const hbs = {};
            history.forEach(h => { if (h?.status && !hbs[h.status]) hbs[h.status] = h; });
            if (!hbs.pending) hbs.pending = { date: order.created_at };
            if (!hbs.confirmed && (order.payment_status === 'paid' || ['paid', 'completed'].includes(order.status))) {
              hbs.confirmed = { date: order.paid_at || order.created_at };
            }
            if (!hbs.invoiced && order.invoice_url) hbs.invoiced = { date: order.invoiced_at || order.created_at };
            const statusToStage = { 'on-hold': 'pending', paid: 'confirmed', processing: 'preparing', completed: 'delivered' };
            const effectiveStatus = statusToStage[order.status] || order.status;
            const order_idx = STAGES.findIndex(s => s.key === effectiveStatus);
            const currentIdx = Math.max(
              order_idx,
              ...STAGES.map((s, i) => hbs[s.key] ? i : -1),
              0
            );
            return (
              <div className="flex items-start gap-0 overflow-x-auto pb-1">
                {STAGES.map((stage, idx) => {
                  const reached = idx <= currentIdx;
                  const isCurrent = idx === currentIdx;
                  const isPast = idx < currentIdx;
                  const isLast = idx === STAGES.length - 1;
                  const entry = hbs[stage.key];
                  const dateStr = entry?.date
                    ? new Date(entry.date).toLocaleDateString('fr-FR', { day: '2-digit', month: 'short' })
                    + ' ' + new Date(entry.date).toLocaleTimeString('fr-FR', { hour: '2-digit', minute: '2-digit' })
                    : null;
                  return (
                    <div key={stage.key} className="flex-1 min-w-[130px] flex flex-col items-center relative" data-testid={`stage-${stage.key}`}>
                      {/* Connector line on top spanning to next */}
                      {!isLast && (
                        <div className={`absolute top-4 left-1/2 w-full h-0.5 ${isPast ? 'bg-emerald-500' : 'bg-slate-200'}`} />
                      )}
                      {/* Circle button */}
                      <button
                        type="button"
                        onClick={() => !updating && !isCurrent && updateStatus(stage.key, `Passage à l'étape "${stage.label}"`)}
                        disabled={updating || isCurrent}
                        className={`relative z-10 w-8 h-8 rounded-full flex items-center justify-center transition-all shrink-0 ${
                          reached
                            ? 'bg-emerald-500 text-white shadow-md hover:scale-105'
                            : 'bg-white border-2 border-slate-300 text-slate-400 hover:border-slate-500'
                        } ${isCurrent ? 'ring-4 ring-emerald-200' : ''}`}
                        title={isCurrent ? 'Étape actuelle' : `Marquer comme "${stage.label}"`}
                      >
                        {reached ? <CheckCircle size={18} weight="fill" /> : <span className="text-[11px] font-bold">{idx + 1}</span>}
                      </button>
                      {/* Label */}
                      <p className={`text-[12px] font-bold mt-2 text-center px-1 ${reached ? 'text-slate-900' : 'text-slate-400'}`}>{stage.label}</p>
                      {dateStr ? (
                        <p className="text-[10px] text-slate-400 mt-0.5 text-center">{dateStr}</p>
                      ) : (
                        <p className="text-[10px] text-slate-300 mt-0.5 text-center italic">{stage.note}</p>
                      )}
                      {isCurrent && (
                        <span className="text-[9px] uppercase tracking-wide font-bold text-emerald-600 mt-0.5">actuel</span>
                      )}
                      {/* Stripe verify shortcut on the "Paiement vérifié" stage */}
                      {stage.key === 'confirmed' && order.stripe_session_id && (
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={verifyStripe}
                          disabled={updating}
                          className="mt-2 h-6 px-2 text-[10px] gap-1 border-[#635BFF]/40 text-[#635BFF] hover:bg-[#635BFF]/5"
                          data-testid="verify-stripe-btn"
                        >
                          {updating ? <CircleNotch size={10} className="animate-spin" /> : <CheckCircle size={10} weight="fill" />}
                          {reached ? 'Re-vérifier' : 'Vérifier Stripe'}
                        </Button>
                      )}
                    </div>
                  );
                })}
              </div>
            );
          })()}
          {/* Tracking number inline + exception actions */}
          <div className="mt-4 pt-4 border-t flex flex-col sm:flex-row gap-3 items-stretch sm:items-end">
            <div className="flex-1 min-w-[180px]">
              <Label className="text-xs">N° de suivi (transporteur)</Label>
              <Input value={trackingNumber} onChange={e => setTrackingNumber(e.target.value)} placeholder="Optionnel" data-testid="tracking-number-input" className="rounded-xl" />
            </div>
            <div className="flex gap-2 flex-wrap">
              {['delivered', 'completed'].includes(order.status) && (
                <Button
                  variant="outline"
                  onClick={requestReview}
                  disabled={updating}
                  className="text-amber-700 border-amber-300 hover:bg-amber-50 rounded-xl"
                  data-testid="request-review-btn"
                  title={order.review_requested_at ? `Déjà envoyée le ${new Date(order.review_requested_at).toLocaleString('fr-FR')}` : 'Envoyer un email de demande d\'avis au client'}
                >
                  <Star size={14} className="mr-1.5" weight={order.review_requested_at ? 'fill' : 'regular'} />
                  {order.review_requested_at ? 'Renvoyer l\'avis' : 'Demander un avis'}
                </Button>
              )}
              <Button
                variant="outline"
                onClick={() => updateStatus('cancelled', 'Commande annulée')}
                disabled={updating || order.status === 'cancelled'}
                className="text-red-700 border-red-200 hover:bg-red-50 rounded-xl"
                data-testid="cancel-order-btn"
              >
                <XCircle size={14} className="mr-1.5" /> Annuler
              </Button>
              <Button
                variant="outline"
                onClick={() => updateStatus('refunded', 'Commande remboursée')}
                disabled={updating || order.status === 'refunded'}
                className="text-orange-700 border-orange-200 hover:bg-orange-50 rounded-xl"
                data-testid="refund-order-btn"
              >
                <XCircle size={14} className="mr-1.5" /> Remboursée
              </Button>
            </div>
          </div>
        </CardContent>
      </Card>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Left: Order info */}
        <div className="lg:col-span-2 space-y-5">
          {/* Items */}
          <Card>
            <CardHeader><CardTitle className="text-base">Articles ({order.items?.length || 0})</CardTitle></CardHeader>
            <CardContent className="p-0">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Produit</TableHead>
                    <TableHead>Qté</TableHead>
                    <TableHead>Prix unit.</TableHead>
                    <TableHead className="text-right">Total</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                {(order.items || []).map((item, i) => {
                  const productHref = item.product_id ? `/admin/products/${item.product_id}/edit` : null;
                  return (
                    <TableRow key={i}>
                      <TableCell>
                        <div className="flex items-center gap-3">
                          {item.image && <img src={item.image} alt="" className="w-10 h-10 rounded object-cover" />}
                          <div>
                            {productHref ? (
                              <a
                                href={productHref}
                                className="font-medium text-sm text-[#0052FF] hover:underline"
                                title="Modifier ce produit"
                                data-testid={`order-item-link-${i}`}
                              >
                                {item.name}
                              </a>
                            ) : (
                              <p className="font-medium text-sm">{item.name}</p>
                            )}
                            {item.variation && <p className="text-xs text-slate-500">{Object.entries(item.variation).map(([k, v]) => `${k}: ${v}`).join(', ')}</p>}
                          </div>
                        </div>
                      </TableCell>
                      <TableCell>{item.quantity}</TableCell>
                      <TableCell>{item.price?.toFixed(2)} €</TableCell>
                      <TableCell className="text-right font-medium">{(item.price * item.quantity).toFixed(2)} €</TableCell>
                    </TableRow>
                  );
                })}
                </TableBody>
              </Table>
              <div className="border-t p-4 space-y-1 text-right">
                {(() => {
                  // Compute TVA dynamically from items (prices are TTC)
                  const items = order.items || [];
                  const vatByRate = {};
                  let totalHt = 0;
                  let totalTtcItems = 0;
                  items.forEach(it => {
                    const priceTtc = (it.price || 0) * (it.quantity || 1);
                    const rate = (typeof it.tax_rate === 'number' && it.tax_rate > 0) ? it.tax_rate : 5.5;
                    const lineHt = priceTtc / (1 + rate / 100);
                    const lineVat = priceTtc - lineHt;
                    totalTtcItems += priceTtc;
                    totalHt += lineHt;
                    vatByRate[rate] = (vatByRate[rate] || 0) + lineVat;
                  });
                  const totalVat = Object.values(vatByRate).reduce((a, b) => a + b, 0);
                  const rates = Object.keys(vatByRate).sort((a, b) => parseFloat(a) - parseFloat(b));
                  return (
                    <>
                      <div className="flex justify-between text-sm"><span>Sous-total HT</span><span>{totalHt.toFixed(2)} €</span></div>
                      {order.discount_amount > 0 && <div className="flex justify-between text-sm text-emerald-600"><span>Réduction ({order.promo_code})</span><span>-{order.discount_amount?.toFixed(2)} €</span></div>}
                      {order.loyalty_discount > 0 && <div className="flex justify-between text-sm text-purple-600"><span>Remise fidélité ({order.loyalty_tier_name})</span><span>-{order.loyalty_discount?.toFixed(2)} €</span></div>}
                      {totalVat > 0 && rates.length === 1 && (
                        <div className="flex justify-between text-sm text-slate-600" data-testid="order-tva-line">
                          <span>TVA ({rates[0]}%)</span>
                          <span>{totalVat.toFixed(2)} €</span>
                        </div>
                      )}
                      {totalVat > 0 && rates.length > 1 && (
                        <>
                          {rates.map(r => (
                            <div key={r} className="flex justify-between text-sm text-slate-600" data-testid={`order-tva-line-${r}`}>
                              <span>Dont TVA {r}%</span>
                              <span>{vatByRate[r].toFixed(2)} €</span>
                            </div>
                          ))}
                          <div className="flex justify-between text-sm text-slate-700 font-medium" data-testid="order-tva-line">
                            <span>TVA totale</span>
                            <span>{totalVat.toFixed(2)} €</span>
                          </div>
                        </>
                      )}
                      <div className="flex justify-between text-sm"><span>Livraison</span><span>{order.shipping_cost > 0 ? `${order.shipping_cost?.toFixed(2)} €` : 'Gratuit'}</span></div>
                      <div className="flex justify-between font-bold text-lg pt-2 border-t"><span>Total TTC</span><span>{order.total?.toFixed(2)} €</span></div>
                    </>
                  );
                })()}
              </div>
            </CardContent>
          </Card>

          {/* Update Status block removed — the timeline stages above are now clickable to advance the order status. Cancellation/refund handled by exception buttons under the timeline. */}

          {/* History Timeline */}
          <Card>
            <CardHeader>
              <CardTitle className="text-base flex items-center gap-2"><Timer size={18} />Historique ({history.length})</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              {/* Add note */}
              <div className="flex gap-2">
                <Input value={historyNote} onChange={e => setHistoryNote(e.target.value)} placeholder="Ajouter une note..." onKeyDown={e => e.key === 'Enter' && addHistory()} data-testid="history-note-input" />
                <Button onClick={addHistory} disabled={adding} size="sm" className="bg-[#0052FF]">
                  {adding ? <CircleNotch className="animate-spin" size={14} /> : <Plus size={14} />}
                </Button>
              </div>

              {/* Timeline */}
              {history.length === 0 ? (
                <p className="text-sm text-slate-500 text-center py-4">Aucun historique</p>
              ) : (
                <div className="relative ml-3">
                  <div className="absolute left-1.5 top-0 bottom-0 w-0.5 bg-slate-200" />
                  {[...history].reverse().map((entry, i) => {
                    const entryStatus = statusConfig[entry.status] || {};
                    const isAttempt = entry.status === 'payment_attempt';
                    const isFailure = isAttempt && (entry.stripe_status === 'failed' || (entry.notes || '').toLowerCase().includes('refusé') || (entry.notes || '').toLowerCase().includes('échec'));
                    const dotColor = isFailure ? 'bg-red-500'
                      : isAttempt ? 'bg-amber-500'
                      : entryStatus.color?.split(' ')[0] || 'bg-slate-300';
                    const bg = isFailure ? 'bg-red-50 border border-red-200'
                      : isAttempt ? 'bg-amber-50 border border-amber-200'
                      : 'bg-slate-50';
                    const label = isAttempt ? 'Tentative de paiement' : (entryStatus.label || entry.status);
                    return (
                      <div key={i} className="relative pl-8 pb-4" data-testid={`history-entry-${i}`}>
                        <div className={`absolute left-0 top-1 w-3.5 h-3.5 rounded-full border-2 border-white ${dotColor}`} />
                        <div className={`rounded-lg p-3 ${bg}`}>
                          <div className="flex items-center justify-between mb-1">
                            <span className={`text-xs font-semibold ${isFailure ? 'text-red-700' : isAttempt ? 'text-amber-700' : 'text-slate-700'}`}>{label}</span>
                            <span className="text-xs text-slate-400">{new Date(entry.date).toLocaleString('fr-FR')}</span>
                          </div>
                          {entry.notes && <p className={`text-sm ${isFailure ? 'text-red-800' : isAttempt ? 'text-amber-800' : 'text-slate-600'}`}>{entry.notes}</p>}
                          {entry.stripe_status && <p className="text-[10px] text-slate-400 mt-0.5 font-mono">stripe.payment_status = {entry.stripe_status}</p>}
                          {entry.user_name && <p className="text-xs text-slate-400 mt-1">Par {entry.user_name}</p>}
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
            </CardContent>
          </Card>
        </div>

        {/* Right: Customer & Shipping */}
        <div className="space-y-5">
          {(() => {
            const ship = order.shipping_address || {};
            const bill = order.billing_address || {};
            // Fallback: use billing if shipping is empty (legacy WC orders)
            const hasShip = ship.first_name || ship.last_name || ship.address || ship.address_1;
            const a = hasShip ? ship : bill;
            const first = a.first_name || '';
            const last = a.last_name || '';
            const phone = a.phone || bill.phone || order.customer_phone;
            const email = a.email || bill.email || order.customer_email;
            const addr = a.address || a.address_1 || '';
            const addr2 = a.address_2 || '';
            const pc = a.postal_code || a.postcode || '';
            const city = a.city || '';
            const fullName = `${first} ${last}`.trim();
            return (
              <>
                <Card>
                  <CardHeader><CardTitle className="text-base">Client</CardTitle></CardHeader>
                  <CardContent className="space-y-1.5">
                    {order.user_id ? (
                      <Link
                        to={`/admin/customers/${order.user_id}`}
                        className="font-semibold text-blue-700 hover:underline inline-flex items-center gap-1"
                        title="Voir la fiche client"
                        data-testid="order-customer-link"
                      >
                        {fullName || email || '—'} <CaretRight size={14} weight="bold" />
                      </Link>
                    ) : (
                      <p className="font-semibold text-slate-900">{fullName || '—'}</p>
                    )}
                    {email && <p className="text-sm text-slate-500 break-all">📧 {email}</p>}
                    {phone && (
                      <a href={`tel:${phone}`} className="text-sm text-blue-600 hover:underline block" data-testid="customer-phone">
                        📞 {phone}
                      </a>
                    )}
                  </CardContent>
                </Card>

                <Card>
                  <CardHeader><CardTitle className="text-base flex items-center gap-2"><MapPin size={16} />{hasShip ? 'Adresse de livraison' : 'Adresse de facturation'}</CardTitle></CardHeader>
                  <CardContent className="text-sm text-slate-600 space-y-1">
                    {fullName && <p className="font-medium text-slate-800">{fullName}</p>}
                    {addr && <p>{addr}</p>}
                    {addr2 && <p>{addr2}</p>}
                    {(pc || city) && <p>{pc} {city}</p>}
                  </CardContent>
                </Card>

                {hasShip && bill.address_1 && (
                  <Card>
                    <CardHeader><CardTitle className="text-base flex items-center gap-2"><MapPin size={16} />Adresse de facturation</CardTitle></CardHeader>
                    <CardContent className="text-sm text-slate-600 space-y-1">
                      <p className="font-medium text-slate-800">{bill.first_name} {bill.last_name}</p>
                      <p>{bill.address_1}</p>
                      {bill.address_2 && <p>{bill.address_2}</p>}
                      <p>{bill.postcode || bill.postal_code} {bill.city}</p>
                    </CardContent>
                  </Card>
                )}
              </>
            );
          })()}

          {(() => {
            const shipKind = order.shipping_kind || (
              /retrait|pickup|magasin|local_pickup|boutique/i.test(order.shipping_method_title || order.shipping_method || '') ? 'pickup'
              : /relais/i.test(order.shipping_method_title || order.shipping_method || '') ? 'point_relay'
              : (order.shipping_method ? 'delivery' : null)
            );
            const isPickup = shipKind === 'pickup';
            return (
              <Card>
                <CardHeader>
                  <CardTitle className="text-base flex items-center gap-2">
                    {isPickup ? <><Package size={16} />Retrait boutique</> : <><Truck size={16} />Livraison</>}
                  </CardTitle>
                </CardHeader>
                <CardContent className="text-sm space-y-3">
                  {isPickup ? (
                    <>
                      <div className="p-2.5 bg-amber-50 border border-amber-200 rounded-lg space-y-1">
                        <p className="text-xs font-semibold text-amber-700 uppercase">Le client vient retirer</p>
                        <p className="text-amber-900 font-medium">🏪 Retrait en boutique — pas de livraison à préparer</p>
                      </div>
                      {order.delivery_date && (
                        <div className="p-2.5 bg-blue-50 border border-blue-200 rounded-lg">
                          <p className="text-xs font-semibold text-blue-700 uppercase mb-1">Date de retrait souhaitée</p>
                          <p className="font-bold text-blue-900">{new Date(order.delivery_date + 'T00:00:00').toLocaleDateString('fr-FR', { weekday: 'long', day: 'numeric', month: 'long', year: 'numeric' })}</p>
                          {order.delivery_slot && <p className="text-blue-600 text-xs mt-0.5">Créneau : {order.delivery_slot}</p>}
                        </div>
                      )}
                    </>
                  ) : (
                    <>
                      {order.delivery_address && order.delivery_address.city ? (
                        <div className="p-2.5 bg-amber-50 border border-amber-200 rounded-lg space-y-1">
                          <p className="text-xs font-semibold text-amber-700 uppercase">Adresse de livraison (différente)</p>
                          {order.delivery_address.address && <p className="text-slate-700">{order.delivery_address.address}</p>}
                          <p className="font-medium text-slate-800">{order.delivery_address.postal_code} {order.delivery_address.city}</p>
                        </div>
                      ) : (
                        <div className="p-2.5 bg-slate-50 rounded-lg space-y-1">
                          <p className="text-xs font-semibold text-slate-500 uppercase">Livraison à</p>
                          <p className="text-slate-700">{order.shipping_address?.address}</p>
                          <p className="font-medium text-slate-800">{order.shipping_address?.postal_code} {order.shipping_address?.city}</p>
                        </div>
                      )}
                      {order.delivery_date ? (
                        <div className="p-2.5 bg-blue-50 border border-blue-200 rounded-lg" data-testid="delivery-date-block">
                          <p className="text-xs font-semibold text-blue-700 uppercase mb-1">Date de livraison souhaitée</p>
                          <p className="font-bold text-blue-900">{new Date(order.delivery_date + 'T00:00:00').toLocaleDateString('fr-FR', { weekday: 'long', day: 'numeric', month: 'long', year: 'numeric' })}</p>
                          {order.delivery_slot && <p className="text-blue-600 text-xs mt-0.5">Créneau : {order.delivery_slot}</p>}
                        </div>
                      ) : (
                        <div className="p-2.5 bg-slate-50 border border-dashed border-slate-200 rounded-lg" data-testid="delivery-date-block">
                          <p className="text-xs font-semibold text-slate-500 uppercase mb-1">Date de livraison souhaitée</p>
                          <p className="text-slate-400 italic text-xs">Non précisée par le client</p>
                        </div>
                      )}
                      <p className="capitalize text-slate-500">{order.shipping_method?.replace(/_/g, ' ')}</p>
                      {order.tracking_number && (
                        <div className="p-2 bg-blue-50 rounded-lg">
                          <p className="text-xs text-blue-600 font-medium">N° de suivi</p>
                          <p className="text-blue-800 font-mono">{order.tracking_number}</p>
                        </div>
                      )}
                    </>
                  )}
                </CardContent>
              </Card>
            );
          })()}

          <Card>
            <CardHeader><CardTitle className="text-base flex items-center gap-2"><CurrencyEur size={16} />Paiement</CardTitle></CardHeader>
            <CardContent className="text-sm space-y-2">
              <p className="capitalize font-medium">{order.payment_method === 'stripe' ? 'Carte bancaire' : order.payment_method === 'paypal' ? 'PayPal' : order.payment_method === 'bank_transfer' ? 'Virement bancaire' : order.payment_method}</p>
              {order.payment_status && (
                <Badge className={order.payment_status === 'paid' ? 'bg-emerald-100 text-emerald-800' : 'bg-amber-100 text-amber-800'}>
                  {order.payment_status === 'paid' ? 'Payé' : order.payment_status === 'pending' ? 'En attente' : order.payment_status}
                </Badge>
              )}
            </CardContent>
          </Card>

          {/* PDF Downloads */}
          <Card>
            <CardHeader><CardTitle className="text-base flex items-center gap-2"><FilePdf size={16} />Documents</CardTitle></CardHeader>
            <CardContent className="space-y-2">
              <Button
                variant="outline"
                className="w-full justify-start gap-2"
                data-testid="download-invoice-btn"
                onClick={() => downloadPdf(`${API_URL}/api/stores/${storeId}/orders/${order.id}/invoice`, `facture_${order.order_number}.pdf`)}
              >
                <Download size={16} /> Facture PDF
              </Button>
              <Button
                variant="outline"
                className="w-full justify-start gap-2"
                data-testid="download-delivery-note-btn"
                onClick={() => downloadPdf(`${API_URL}/api/stores/${storeId}/orders/${order.id}/delivery-note`, `bon_livraison_${order.order_number}.pdf`)}
              >
                <Download size={16} /> Bon de livraison PDF
              </Button>
            </CardContent>
          </Card>
        </div>
      </div>
    </div>
  );
};

// ======== MAIN ORDERS PAGE ========
const OrdersPage = () => {
  const { currentStore } = useStore();
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [statusFilter, setStatusFilter] = useState('all');
  const [pickupOnly, setPickupOnly] = useState(false);
  const [bulkVerifying, setBulkVerifying] = useState(false);

  const runBulkStripeVerify = async () => {
    if (!currentStore) return;
    setBulkVerifying(true);
    try {
      const { data } = await axios.post(
        `${API_URL}/api/stores/${currentStore.id}/orders/verify-stripe-bulk`,
        {}, { withCredentials: true, timeout: 120000 }
      );
      if (data.checked === 0) {
        toast.info('Toutes les commandes Stripe actives sont déjà vérifiées ✅');
      } else {
        const parts = [`${data.confirmed.length} payée(s) confirmée(s)`];
        if (data.not_paid.length) parts.push(`⚠️ ${data.not_paid.length} NON payée(s): ${data.not_paid.join(', ')}`);
        if (data.errors.length) parts.push(`${data.errors.length} erreur(s)`);
        (data.not_paid.length ? toast.warning : toast.success)(`Vérification Stripe : ${parts.join(' — ')}`, { duration: 8000 });
        if (data.remaining > 0) toast.info(`${data.remaining} commande(s) restante(s) — relancez la vérification`);
      }
      fetchOrders();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Erreur lors de la vérification en masse');
    } finally { setBulkVerifying(false); }
  };
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');
  const [productFilter, setProductFilter] = useState('');
  const [sortBy, setSortBy] = useState('created_desc');
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedOrder, setSelectedOrder] = useState(null);
  const [selectedIds, setSelectedIds] = useState(new Set());
  const [bulkAction, setBulkAction] = useState('');
  const [bulkRunning, setBulkRunning] = useState(false);

  const fetchOrders = useCallback(async () => {
    if (!currentStore) return;
    try {
      // Fetch all orders — filtering by tab (bucket) and pickup toggle is done client-side
      const response = await axios.get(`${API_URL}/api/stores/${currentStore.id}/orders`, { withCredentials: true });
      setOrders(response.data);
    } catch { toast.error('Erreur lors du chargement'); }
    finally { setLoading(false); }
  }, [currentStore]);

  useEffect(() => { fetchOrders(); }, [fetchOrders]);

  const refreshAndReload = async () => {
    await fetchOrders();
    if (selectedOrder) {
      try {
        const resp = await axios.get(`${API_URL}/api/stores/${currentStore.id}/orders/${selectedOrder.id}`, { withCredentials: true });
        setSelectedOrder(resp.data);
      } catch { }
    }
  };

  if (!currentStore) return <div className="text-center py-12"><p className="text-slate-500">Sélectionnez une boutique</p></div>;

  if (selectedOrder) {
    return <OrderDetail order={selectedOrder} storeId={currentStore.id} onClose={() => setSelectedOrder(null)} onUpdate={refreshAndReload} />;
  }

  // Map raw order.status onto simplified buckets used in tabs
  // Intent = intention de commande (paiement en ligne jamais abouti)
  // Archives = delivered + cancelled + refunded (terminal states)
  const isPaidOrder = (o) => {
    const s = (o.status || '').toLowerCase();
    if (o.payment_status === 'paid') return true;
    if (['paid', 'confirmed', 'preparing', 'processing', 'shipping', 'shipped', 'delivered', 'completed'].includes(s)) return true;
    // paiement hors-ligne (à la livraison, virement, chèque…) = vraie commande
    return /cod|cash|cheque|chèque|virement|sur_place|livraison/i.test(o.payment_method || '');
  };
  const bucketOf = (o) => {
    const s = (o.status || '').toLowerCase();
    if (o.is_test) return 'test';
    if (['cancelled', 'refunded', 'failed'].includes(s)) return 'archived';
    if (['delivered', 'completed'].includes(s)) return 'archived';
    if (!isPaidOrder(o)) return 'intent';
    if (['shipping', 'shipped'].includes(s)) return 'shipping';
    if (['preparing', 'processing'].includes(s)) return 'preparing';
    return 'pending'; // pending, on-hold, paid, confirmed
  };
  const isPickup = (o) => /retrait|pickup|magasin|local_pickup|boutique/i.test(
    o.shipping_method_title || o.shipping_method || ''
  );

  // Build tab counts once (apply pickup filter to counts as well so tabs stay coherent)
  const preFiltered = pickupOnly ? orders.filter(isPickup) : orders;
  const bucketCounts = preFiltered.reduce((acc, o) => {
    const b = bucketOf(o);
    acc[b] = (acc[b] || 0) + 1;
    return acc;
  }, {});
  const activeCount = (bucketCounts.pending || 0) + (bucketCounts.preparing || 0) + (bucketCounts.shipping || 0);

  const q = searchQuery.trim().toLowerCase();
  const filtered = preFiltered.filter(o => {
    const b = bucketOf(o);
    if (statusFilter === 'all') {
      // "En cours" = only paid, non-delivered orders (intentions, tests & archives exclus)
      if (b === 'archived' || b === 'intent' || b === 'test') return false;
    } else if (statusFilter === 'archived') {
      if (b !== 'archived') return false;
    } else if (b !== statusFilter) {
      return false;
    }
    if (!q) return true;
    const hay = [
      o.order_number, o.customer_email,
      o.billing_address?.first_name, o.billing_address?.last_name,
      o.shipping_address?.first_name, o.shipping_address?.last_name,
      o.customer_phone, o.tracking_code, o.tracking_number,
    ].filter(Boolean).join(' ').toLowerCase();
    return hay.includes(q);
  });

  // Sort the filtered orders. Orders missing a delivery date are pushed to the end
  // when sorting by delivery date so the actionable ones surface first.
  const sortedOrders = (() => {
    const list = [...filtered];
    const cmpDelivery = (a, b, dir) => {
      const da = a.delivery_date || '';
      const db = b.delivery_date || '';
      if (!da && !db) return new Date(b.created_at) - new Date(a.created_at);
      if (!da) return 1;
      if (!db) return -1;
      return dir === 'asc' ? da.localeCompare(db) : db.localeCompare(da);
    };
    if (sortBy === 'delivery_asc') list.sort((a, b) => cmpDelivery(a, b, 'asc'));
    else if (sortBy === 'delivery_desc') list.sort((a, b) => cmpDelivery(a, b, 'desc'));
    else if (sortBy === 'total_desc') list.sort((a, b) => (b.total || 0) - (a.total || 0));
    else if (sortBy === 'created_asc') list.sort((a, b) => new Date(a.created_at) - new Date(b.created_at));
    return list;
  })();

  const statusTabs = [
    { key: 'all',        label: 'En cours',          count: activeCount },
    { key: 'pending',    label: 'À vérifier',        count: bucketCounts.pending || 0 },
    { key: 'preparing',  label: 'En préparation',    count: bucketCounts.preparing || 0 },
    { key: 'shipping',   label: 'Expédiées',         count: bucketCounts.shipping || 0 },
    { key: 'intent',     label: 'Intentions',        count: bucketCounts.intent || 0 },
    { key: 'test',       label: 'Tests',             count: bucketCounts.test || 0 },
    { key: 'archived',   label: 'Livrées & annulées', count: bucketCounts.archived || 0 },
  ];

  const toggleId = (id, checked) => {
    setSelectedIds(prev => {
      const next = new Set(prev);
      if (checked) next.add(id); else next.delete(id);
      return next;
    });
  };
  const toggleAllVisible = (checked) => {
    setSelectedIds(prev => {
      const next = new Set(prev);
      if (checked) sortedOrders.forEach(o => next.add(o.id));
      else sortedOrders.forEach(o => next.delete(o.id));
      return next;
    });
  };

  const runBulkAction = async () => {
    if (!bulkAction || selectedIds.size === 0) return;
    if (!window.confirm(`Appliquer "${bulkAction}" sur ${selectedIds.size} commande(s) ?`)) return;
    setBulkRunning(true);
    const ids = Array.from(selectedIds);
    const noteMap = {
      confirmed: 'Paiement vérifié (action groupée)',
      preparing: 'Passée en préparation (action groupée)',
      shipping: 'Marquée expédiée (action groupée)',
      delivered: 'Marquée livrée (action groupée)',
      cancelled: 'Commande annulée (action groupée)',
      refunded: 'Commande remboursée (action groupée)',
    };
    let ok = 0, fail = 0;
    for (const id of ids) {
      try {
        await axios.put(
          `${API_URL}/api/stores/${currentStore.id}/orders/${id}/status`,
          { status: bulkAction, notes: noteMap[bulkAction] || `Statut ${bulkAction}` },
          { withCredentials: true }
        );
        ok++;
      } catch { fail++; }
    }
    setBulkRunning(false);
    setSelectedIds(new Set());
    setBulkAction('');
    if (fail === 0) toast.success(`${ok} commande(s) mise(s) à jour. Clients notifiés.`);
    else toast.error(`${ok} OK · ${fail} erreur(s)`);
    fetchOrders();
  };

  return (
    <div className="space-y-4 md:space-y-5" data-testid="orders-page">
      {/* Title */}
      <div className="flex items-start justify-between gap-3">
        <div>
          <p className="text-[10px] md:text-xs uppercase tracking-widest text-slate-400 font-semibold">Gestion</p>
          <h1 className="text-2xl md:text-3xl font-black tracking-tight text-slate-900">Commandes</h1>
        </div>
        <Button variant="outline" onClick={() => {
          const params = new URLSearchParams();
          if (dateFrom) params.set('date_from', dateFrom);
          if (dateTo) params.set('date_to', dateTo);
          if (statusFilter && statusFilter !== 'all') params.set('status', statusFilter);
          if (productFilter) params.set('product_id', productFilter);
          const url = `${API_URL}/api/admin/orders-export?${params.toString()}`;
          const a = document.createElement('a'); a.href = url; a.click();
        }} className="gap-2 rounded-full text-xs md:text-sm shrink-0" data-testid="export-orders-csv">
          <Download size={14} /> <span className="hidden sm:inline">Exporter</span> CSV
        </Button>
      </div>

      {/* Search bar */}
      <div className="relative">
        <MagnifyingGlass size={18} className="absolute left-4 top-1/2 -translate-y-1/2 text-slate-400" />
        <Input
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          placeholder="Rechercher client, email, n° commande..."
          className="pl-11 h-11 md:h-12 bg-white rounded-2xl border-slate-200 focus-visible:ring-2 focus-visible:ring-[#0052FF]/20 text-sm"
          data-testid="orders-search"
        />
      </div>

      {/* Status tabs (pills) — scroll horizontally on mobile */}
      <div className="flex items-center gap-2 overflow-x-auto -mx-4 px-4 md:mx-0 md:px-0 md:flex-wrap pb-1">
        {statusTabs.map(tab => (
          <button
            key={tab.key}
            onClick={() => setStatusFilter(tab.key)}
            className={`shrink-0 px-3 md:px-4 py-1.5 md:py-2 rounded-full text-xs md:text-sm font-semibold transition-all whitespace-nowrap ${
              statusFilter === tab.key
                ? (tab.key === 'archived' ? 'bg-slate-500 text-white shadow-sm' : 'bg-slate-900 text-white shadow-sm')
                : (tab.key === 'archived'
                    ? 'bg-slate-50 text-slate-500 hover:bg-slate-100 border border-slate-200'
                    : 'bg-white text-slate-600 hover:bg-slate-100 border border-slate-200')
            }`}
            data-testid={`status-tab-${tab.key}`}
          >
            {tab.key === 'archived' && '📦 '}{tab.label} <span className={`ml-1 text-[10px] md:text-xs ${statusFilter === tab.key ? 'text-white/70' : 'text-slate-400'}`}>({tab.count})</span>
          </button>
        ))}
        <div className="h-6 w-px bg-slate-200 mx-1 hidden md:block" />
        <button
          onClick={() => setPickupOnly(v => !v)}
          className={`shrink-0 px-3 md:px-4 py-1.5 md:py-2 rounded-full text-xs md:text-sm font-semibold transition-all whitespace-nowrap ${
            pickupOnly
              ? 'bg-emerald-600 text-white shadow-sm'
              : 'bg-white text-emerald-700 hover:bg-emerald-50 border border-emerald-200'
          }`}
          data-testid="filter-pickup-only"
          title="Afficher uniquement les commandes en retrait boutique"
        >
          🏪 Retrait uniquement
        </button>
        <button
          onClick={runBulkStripeVerify}
          disabled={bulkVerifying}
          className="shrink-0 px-3 md:px-4 py-1.5 md:py-2 rounded-full text-xs md:text-sm font-semibold transition-all whitespace-nowrap bg-white text-indigo-700 hover:bg-indigo-50 border border-indigo-200 disabled:opacity-50"
          data-testid="bulk-verify-stripe-btn"
          title="Vérifier auprès de Stripe que les commandes affichées sont bien payées"
        >
          {bulkVerifying ? '⏳ Vérification…' : '🔎 Vérifier paiements Stripe'}
        </button>
      </div>

      {/* Date + sort filters — stacked on mobile */}
      <div className="grid grid-cols-2 md:flex md:items-center md:gap-2 gap-2 md:justify-end">
        <Input type="date" value={dateFrom} onChange={(e) => setDateFrom(e.target.value)} className="h-9 text-xs md:text-sm md:w-[130px]" data-testid="date-from" />
        <Input type="date" value={dateTo} onChange={(e) => setDateTo(e.target.value)} className="h-9 text-xs md:text-sm md:w-[130px]" data-testid="date-to" />
        <Select value={sortBy} onValueChange={setSortBy}>
          <SelectTrigger className="h-9 col-span-2 md:w-[210px] text-xs md:text-sm" data-testid="orders-sort-select"><SelectValue placeholder="Trier par" /></SelectTrigger>
          <SelectContent>
            <SelectItem value="created_desc">📅 Date de commande ↓</SelectItem>
            <SelectItem value="created_asc">📅 Date de commande ↑</SelectItem>
            <SelectItem value="delivery_asc">🚚 Date de livraison ↑</SelectItem>
            <SelectItem value="delivery_desc">🚚 Date de livraison ↓</SelectItem>
            <SelectItem value="total_desc">💰 Montant ↓</SelectItem>
          </SelectContent>
        </Select>
      </div>

      {/* Bulk actions bar (visible when ≥1 order selected) */}
      {selectedIds.size > 0 && (
        <div className="flex items-center gap-3 px-4 py-3 bg-slate-900 text-white rounded-2xl sticky top-2 z-20 shadow-lg" data-testid="bulk-actions-bar">
          <span className="font-semibold text-sm">{selectedIds.size} commande{selectedIds.size > 1 ? 's' : ''} sélectionnée{selectedIds.size > 1 ? 's' : ''}</span>
          <div className="flex-1 flex items-center gap-2 flex-wrap">
            <Select value={bulkAction} onValueChange={setBulkAction}>
              <SelectTrigger className="w-[220px] h-9 bg-white text-slate-900" data-testid="bulk-action-select"><SelectValue placeholder="Choisir une action..." /></SelectTrigger>
              <SelectContent>
                <SelectItem value="confirmed">✓ Marquer Paiement vérifié</SelectItem>
                <SelectItem value="preparing">📦 Passer en préparation</SelectItem>
                <SelectItem value="shipping">🚚 Marquer expédiée</SelectItem>
                <SelectItem value="delivered">✅ Marquer livrée</SelectItem>
                <SelectItem value="cancelled">❌ Annuler</SelectItem>
                <SelectItem value="refunded">↩️ Rembourser</SelectItem>
              </SelectContent>
            </Select>
            <Button
              onClick={runBulkAction}
              disabled={!bulkAction || bulkRunning}
              className="bg-emerald-500 hover:bg-emerald-600 h-9 rounded-full text-sm font-semibold"
              data-testid="apply-bulk-action"
            >
              {bulkRunning ? <CircleNotch size={14} className="animate-spin mr-1.5" /> : null}
              Appliquer
            </Button>
          </div>
          <button
            onClick={() => setSelectedIds(new Set())}
            className="text-white/60 hover:text-white text-xs"
            data-testid="clear-selection"
          >
            Tout désélectionner
          </button>
        </div>
      )}

      {/* Cards list */}
      {loading ? (
        <div className="p-12 text-center"><div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[#0052FF] mx-auto" /></div>
      ) : sortedOrders.length === 0 ? (
        <Card><CardContent className="p-16 text-center">
          <ShoppingCart size={48} className="mx-auto text-slate-300 mb-4" />
          <p className="text-slate-500">Aucune commande</p>
        </CardContent></Card>
      ) : (
        <div className="space-y-2.5">
          {sortedOrders.length > 1 && (
            <div className="flex items-center gap-2 px-4 pt-1 pb-2">
              <input
                type="checkbox"
                checked={sortedOrders.every(o => selectedIds.has(o.id))}
                onChange={(e) => toggleAllVisible(e.target.checked)}
                className="w-4 h-4 rounded border-slate-300"
                data-testid="select-all-orders"
              />
              <span className="text-xs text-slate-500">Tout sélectionner ({sortedOrders.length})</span>
            </div>
          )}
          {sortedOrders.map(order => {
            const st = statusConfig[order.status] || statusConfig.pending;
            const paymentPaid = order.payment_status === 'paid' || ['paid', 'completed'].includes(order.status);
            const paymentFailed = order.payment_status === 'failed' || order.status === 'cancelled' || order.status === 'failed';
            const stripeVerified = !!order.stripe_verified_at;
            const paymentLabel = paymentFailed ? 'Échec paiement'
              : stripeVerified ? 'Vérifié Stripe'
              : paymentPaid ? 'Payé'
              : 'En attente';
            const bucket = bucketOf(order);
            const statusBg = paymentPaid && (bucket === 'delivered') ? 'bg-emerald-50 text-emerald-800 border-emerald-200'
              : paymentFailed || bucket === 'cancelled' ? 'bg-red-50 text-red-700 border-red-200'
              : 'bg-emerald-50 text-emerald-800 border-emerald-200';
            const paymentBg = paymentFailed ? 'bg-red-50 text-red-700 border-red-200'
              : paymentPaid ? 'bg-emerald-50 text-emerald-800 border-emerald-200'
              : 'bg-amber-50 text-amber-800 border-amber-200';
            const rawMethod = (order.payment_method || '').toLowerCase();
            const methodLabel =
                rawMethod === 'stripe' || rawMethod.includes('carte') || rawMethod.includes('credit') || rawMethod.includes('crédit') ? 'CB'
              : rawMethod === 'link' ? 'Link'
              : rawMethod === 'paypal' || rawMethod.includes('paypal') ? 'PayPal'
              : rawMethod === 'bank_transfer' || rawMethod.includes('virement') ? 'Virement'
              : rawMethod === 'cod' || rawMethod === 'cash_on_delivery' || rawMethod.includes('espèce') || rawMethod.includes('livraison') ? 'Espèces'
              : rawMethod === 'cheque' || rawMethod.includes('chèque') ? 'Chèque'
              : (order.payment_method || '—');
            const shipKind = order.shipping_kind || (
              /retrait|pickup|magasin|local_pickup|boutique/i.test(order.shipping_method_title || order.shipping_method || '') ? 'pickup'
              : /relais/i.test(order.shipping_method_title || order.shipping_method || '') ? 'point_relay'
              : (order.shipping_method ? 'delivery' : null)
            );
            const shipLabel = shipKind === 'pickup' ? 'Retrait boutique'
              : shipKind === 'point_relay' ? 'Point relais'
              : shipKind === 'delivery' ? (order.shipping_method_title || 'Livraison')
              : null;
            const fullName = `${order.shipping_address?.first_name || order.billing_address?.first_name || ''} ${order.shipping_address?.last_name || order.billing_address?.last_name || ''}`.trim();
            const statusPillLabel = bucket === 'delivered' ? 'Livrée'
              : bucket === 'shipping' ? 'Expédiée'
              : bucket === 'preparing' ? 'En préparation'
              : bucket === 'cancelled' ? (order.status === 'refunded' ? 'Remboursée' : 'Annulée')
              : (paymentPaid ? 'Paiement vérifié' : 'Commande créée');
            return (
              <div
                key={order.id}
                className={`w-full text-left bg-white rounded-2xl border transition-all p-3 md:p-4 flex flex-col md:flex-row md:items-center gap-3 md:gap-4 group ${selectedIds.has(order.id) ? 'border-slate-900 ring-2 ring-slate-900/10' : 'border-slate-200 hover:border-slate-300 hover:shadow-sm'}`}
                data-testid={`order-row-${order.id}`}
              >
                {/* Header row (visible on mobile) */}
                <div className="flex items-center gap-3 md:hidden">
                  <input
                    type="checkbox"
                    checked={selectedIds.has(order.id)}
                    onChange={(e) => toggleId(order.id, e.target.checked)}
                    onClick={(e) => e.stopPropagation()}
                    className="w-4 h-4 rounded border-slate-300 shrink-0"
                  />
                  <p className="text-base font-black text-slate-400">#{order.order_number?.replace(/^ORD-|^WP-/, '') || ''}</p>
                  {order.customer_group?.name && (
                    <span className="px-1.5 py-0.5 rounded text-[9px] font-semibold text-white uppercase" style={{ backgroundColor: order.customer_group.color || '#64748b' }}>
                      {order.customer_group.name}
                    </span>
                  )}
                  <div className="ml-auto flex flex-col gap-1 items-end">
                    <span className={`px-2 py-0.5 rounded-full text-[10px] font-semibold border ${paymentBg} flex items-center gap-1`}>
                      <span className={`w-1.5 h-1.5 rounded-full ${paymentFailed ? 'bg-red-500' : paymentPaid ? 'bg-emerald-500' : 'bg-amber-500'}`} />
                      {paymentLabel}
                    </span>
                    <span className={`px-2 py-0.5 rounded-full text-[10px] font-semibold border ${statusBg}`}>
                      {statusPillLabel}
                    </span>
                  </div>
                </div>

                {/* Desktop checkbox */}
                <input
                  type="checkbox"
                  checked={selectedIds.has(order.id)}
                  onChange={(e) => toggleId(order.id, e.target.checked)}
                  onClick={(e) => e.stopPropagation()}
                  className="hidden md:block w-4 h-4 rounded border-slate-300 shrink-0"
                  data-testid={`order-checkbox-${order.id}`}
                />
                <button
                  type="button"
                  onClick={() => setSelectedOrder(order)}
                  className="flex-1 flex flex-col md:flex-row md:items-center gap-2 md:gap-4 text-left w-full"
                  data-testid={`open-order-${order.id}`}
                >
                {/* Order # + group — hidden on mobile (shown above) */}
                <div className="hidden md:block flex-shrink-0 md:w-[110px]">
                  <p className="text-lg font-black text-slate-400">#{order.order_number?.replace(/^ORD-|^WP-/, '') || ''}</p>
                  {order.customer_group?.name && (
                    <span className="px-1.5 py-0.5 rounded text-[10px] font-semibold text-white uppercase tracking-wide mt-1 inline-block" style={{ backgroundColor: order.customer_group.color || '#64748b' }}>
                      {order.customer_group.name}
                    </span>
                  )}
                </div>
                {/* Middle: customer + meta */}
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 flex-wrap">
                    <p className="font-bold text-slate-900 truncate text-sm md:text-base">{fullName || order.customer_email || '—'}</p>
                    {order.tracking_number && (
                      <span className="text-[10px] text-slate-500 font-mono bg-slate-50 px-1.5 py-0.5 rounded">🏷️ {order.tracking_number}</span>
                    )}
                  </div>
                  <div className="flex items-center gap-1.5 md:gap-2 mt-1 text-[11px] md:text-xs flex-wrap">
                    <span className="text-slate-500">{new Date(order.created_at).toLocaleDateString('fr-FR', { day: '2-digit', month: '2-digit', year: '2-digit' })} {new Date(order.created_at).toLocaleTimeString('fr-FR', { hour: '2-digit', minute: '2-digit' })}</span>
                    <span className="text-slate-300">·</span>
                    <span className="font-bold text-slate-900">{order.total?.toFixed(2)} €</span>
                    <span className="inline-flex items-center"><PaymentMethodBadge method={order.payment_method} /></span>
                    {shipLabel && (
                      <span className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded bg-slate-100 text-slate-600 text-[10px] font-medium">
                        {shipKind === 'pickup' ? '🏪' : shipKind === 'point_relay' ? '📦' : '🚚'} {shipLabel}
                      </span>
                    )}
                    {order.delivery_date && (
                      <span className="inline-flex items-center gap-1 text-[10px] font-semibold text-slate-600">
                        📅 {new Date(order.delivery_date + 'T00:00:00').toLocaleDateString('fr-FR', { day: 'numeric', month: 'short' })}
                      </span>
                    )}
                    {shipKind === 'delivery' && (order.shipping_address?.postal_code || order.shipping_address?.postcode) && (
                      <span className="text-[10px] text-slate-500">📍 {order.shipping_address?.postal_code || order.shipping_address?.postcode} {order.shipping_address?.city || ''}</span>
                    )}
                  </div>
                </div>
                {/* Right: dual status pills + arrow — hidden on mobile (shown above) */}
                <div className="hidden md:flex flex-shrink-0 items-center gap-2">
                  <div className="flex flex-col gap-1 items-end">
                    <span className={`px-3 py-1 rounded-full text-[11px] font-semibold border ${paymentBg} flex items-center gap-1`}>
                      <span className={`w-1.5 h-1.5 rounded-full ${paymentFailed ? 'bg-red-500' : paymentPaid ? 'bg-emerald-500' : 'bg-amber-500'}`} />
                      {paymentLabel}
                    </span>
                    <span className={`px-3 py-1 rounded-full text-[11px] font-semibold border ${statusBg}`}>
                      {statusPillLabel}
                    </span>
                  </div>
                  <CaretRight size={20} className="text-slate-300 group-hover:text-slate-500 transition-colors" />
                </div>
                </button>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
};

export default OrdersPage;
